/**
 * InlineCaptureForm — structured inputs rendered inside the chat area
 * as a card (not a separate pre-chat screen).
 *
 * The bot sends its greeting, then this card appears below it with
 * the configured fields. Once submitted, the captured data is sent
 * via the same onPreChatSubmit callback and the card disappears.
 */

import { useState } from 'preact/hooks';
import { cn } from '../lib/cn';
import type { PreChatFormConfig, PreChatFormField } from '../client/api-types';
import type { PreChatData } from './useChatState';
import { t } from '../lib/i18n';
import { validateFields } from '../lib/field-validation';

interface InlineCaptureFormProps {
  config: PreChatFormConfig;
  brandingColor: string;
  onSubmit: (data: PreChatData) => void;
}

function getInputType(field: PreChatFormField): string {
  switch (field.type) {
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    default:
      return 'text';
  }
}

export function InlineCaptureForm({
  config,
  brandingColor,
  onSubmit,
}: InlineCaptureFormProps) {
  const initial = Object.fromEntries(config.fields.map((f) => [f.name, '']));
  const [values, setValues] = useState<PreChatData>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const next = validateFields(config.fields, values);
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: Event) {
    e.preventDefault();
    if (validate()) {
      onSubmit(values);
    }
  }

  function handleChange(name: string, val: string) {
    setValues((prev) => ({ ...prev, [name]: val }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  return (
    <div class="flex justify-start px-3 mb-[10px] animate-msg-in">
      <div
        class={cn(
          'w-full max-w-[76%] rounded-[18px] rounded-bl-[10px] p-4',
          'bg-w-bb-bg text-w-bb-fg',
          'shadow-[0_1px_1px_rgba(0,0,0,0.08)]',
        )}
      >
        <p class="m-0 text-[13px] text-w-secondary mb-3">
          {t('lets_talk_subtitle')}
        </p>

        <form onSubmit={handleSubmit} novalidate class="flex flex-col gap-2.5">
          {config.fields.map((field) => (
            <div key={field.name} class="flex flex-col gap-0.5">
              <input
                name={field.name}
                type={getInputType(field)}
                value={values[field.name]}
                onInput={(e) =>
                  handleChange(field.name, (e.target as HTMLInputElement).value)
                }
                required={field.required}
                placeholder={`${field.label}${field.required ? ' *' : ''}`}
                class={cn(
                  'rounded-[10px] px-3 py-2 text-[13.5px] text-w-bb-fg',
                  'border outline-none transition-colors duration-150',
                  'bg-w-input placeholder:text-w-secondary/50',
                  errors[field.name]
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-w-border focus:border-[var(--brand)]',
                )}
              />
              {errors[field.name] && (
                <p class="m-0 text-[11px] text-red-500 px-1">
                  {errors[field.name]}
                </p>
              )}
            </div>
          ))}

          <button
            type="submit"
            style={{ background: brandingColor }}
            class={cn(
              'rounded-[10px] px-3 py-2 text-[13.5px] font-semibold text-white',
              'border-0 cursor-pointer w-full',
              'hover:opacity-90 active:scale-[0.98] transition-all duration-150',
            )}
          >
            {t('start_chat')}
          </button>
        </form>
      </div>
    </div>
  );
}
